import React, { useState, useEffect, useRef } from 'react'
import { Box, IconButton, Typography, styled, Menu, MenuItem } from '@mui/material'
import { ArrowBack, MoreVert, RestartAlt } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import ImageComponent from 'ui/components/shared/ImageComponent'
import SkillsChatWrapper from 'ui/containers/myskills/SkillsChatWrapper'
import { POST } from 'api/index'

const ChatContainer = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: '#e5ddd5',
  backgroundImage: 'url("data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%23000000" fill-opacity="0.02"%3E%3Cpath d="M20 20c0 11.046-8.954 20-20 20v20h40V20H20z"/%3E%3C/g%3E%3C/svg%3E")',
  zIndex: 99999,
  display: 'flex',
  flexDirection: 'column',
});

// Header mit fester grüner Farbe und responsivem Safe Area Support
const ChatHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  padding: '0.75rem 1rem',
  // Dynamic padding-top basierend auf Safe Area
  paddingTop: 'max(0.75rem, env(safe-area-inset-top))',
  backgroundColor: '#A1B7A5', // Feste grüne Farbe ohne Verlauf
  color: 'white',
  minHeight: '4rem', // Reduzierte Höhe
  boxShadow: '0 2px 8px rgba(161, 183, 165, 0.3)',
  position: 'relative',
  zIndex: 100,
  width: '100%',
  maxWidth: '100vw',
  boxSizing: 'border-box',
  
  // iPhone spezifische Anpassungen
  '@media (max-width: 390px)': {
    padding: '0.5rem 0.75rem',
    paddingTop: 'max(0.5rem, env(safe-area-inset-top))',
    minHeight: '3.5rem',
  },
  
  // iPhone SE und kleinere Geräte
  '@media (max-width: 375px)': {
    padding: '0.5rem 0.75rem',
    paddingTop: 'max(0.5rem, env(safe-area-inset-top))',
    minHeight: '3.25rem',
  },
  
  // iPhone SE (320px breit)
  '@media (max-width: 320px)': {
    padding: '0.5rem 0.5rem',
    paddingTop: 'max(0.5rem, env(safe-area-inset-top))',
    minHeight: '3rem',
  },
  
  // Landscape Mode - reduzierte Höhe
  '@media (orientation: landscape) and (max-height: 500px)': {
    padding: '0.5rem 1rem',
    paddingTop: 'max(0.5rem, env(safe-area-inset-top))',
    minHeight: '2.75rem',
  }
});

const BackButton = styled(IconButton)({
  color: 'white',
  marginRight: '0.75rem',
  padding: '0.5rem',
  backgroundColor: 'rgba(255, 255, 255, 0.15)',
  borderRadius: '12px',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  width: '40px',
  height: '40px',
  
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    transform: 'translateY(-1px)',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)'
  },
  
  '&:active': {
    transform: 'translateY(0)',
    transition: 'all 0.1s ease'
  },
  
  // iPhone spezifische Größen
  '@media (max-width: 375px)': {
    padding: '0.375rem',
    marginRight: '0.5rem',
    width: '36px',
    height: '36px',
    borderRadius: '10px',
  },
  
  '@media (max-width: 320px)': {
    padding: '0.25rem',
    marginRight: '0.375rem',
    width: '32px',
    height: '32px',
    borderRadius: '8px',
  }
});

const ProfileSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  gap: '0.75rem',
  minWidth: 0, // Wichtig für Text-Overflow
  
  '@media (max-width: 375px)': {
    gap: '0.5rem',
  },
  
  '@media (max-width: 320px)': {
    gap: '0.375rem',
  }
});

const ProfileImage = styled(Box)({
  width: '2.75rem',
  height: '2.75rem',
  borderRadius: '50%',
  overflow: 'hidden',
  backgroundColor: '#ddd',
  flexShrink: 0,
  border: '2px solid rgba(255, 255, 255, 0.4)',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  transition: 'all 0.3s ease',
  
  '&:hover': {
    transform: 'scale(1.05)',
    borderColor: 'rgba(255, 255, 255, 0.6)'
  },
  
  // iPhone spezifische Größen
  '@media (max-width: 375px)': {
    width: '2.5rem',
    height: '2.5rem',
  },
  
  '@media (max-width: 320px)': {
    width: '2.25rem',
    height: '2.25rem',
    border: '1.5px solid rgba(255, 255, 255, 0.4)',
  }
});

const ProfileInfo = styled(Box)({
  flex: 1,
  minWidth: 0, // Wichtig für Text-Overflow
  overflow: 'hidden',
});

const ContactName = styled(Typography)({
  fontSize: '1.125rem',
  fontWeight: 700,
  color: 'white',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
  letterSpacing: '0.025em',
  lineHeight: '1.2',
  
  // iPhone spezifische Größen
  '@media (max-width: 375px)': {
    fontSize: '1rem',
  },
  
  '@media (max-width: 320px)': {
    fontSize: '0.95rem',
    fontWeight: 600,
  }
});

const LastSeen = styled(Typography)({
  fontSize: '0.8rem',
  color: 'rgba(255, 255, 255, 0.9)',
  marginTop: '0.125rem',
  fontWeight: 500,
  textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  
  // iPhone spezifische Größen
  '@media (max-width: 375px)': {
    fontSize: '0.75rem',
    marginTop: '0.1rem',
  },
  
  '@media (max-width: 320px)': {
    fontSize: '0.7rem',
    marginTop: '0.05rem',
  }
});

const MenuButton = styled(IconButton)({
  color: 'white',
  padding: '0.5rem',
  minWidth: '40px',
  minHeight: '40px',
  width: '40px',
  height: '40px',
  borderRadius: '12px',
  backgroundColor: 'rgba(255, 255, 255, 0.15)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    transform: 'translateY(-1px)',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)'
  },
  
  '&:active': {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    transform: 'scale(0.95)',
    transition: 'all 0.1s ease'
  },
  
  // iPhone spezifische Größen
  '@media (max-width: 375px)': {
    padding: '0.375rem',
    minWidth: '36px',
    minHeight: '36px',
    width: '36px',
    height: '36px',
    borderRadius: '10px',
  },
  
  '@media (max-width: 320px)': {
    padding: '0.25rem',
    minWidth: '32px',
    minHeight: '32px',
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    
    '& .MuiSvgIcon-root': {
      fontSize: '20px',
    }
  }
});

// Chat Wrapper ohne Änderungen
const MobileChatWrapper = styled(Box)({
  flex: 1,
  overflow: 'hidden',
  position: 'relative',
  backgroundColor: '#e5ddd5',
  display: 'flex',
  flexDirection: 'column'
});

interface MobileSkillsDetailViewProps {
  skill: any;
  onClose: () => void;
  onDelete?: () => void;
  updateProgress: () => void;
  clearProgress?: () => void;
}

const MobileSkillsDetailView = ({
  skill,
  onClose,
  onDelete,
  updateProgress,
  clearProgress
}: MobileSkillsDetailViewProps) => {
  const { t } = useTranslation()
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [chatKey, setChatKey] = useState(0)


const handleClose = () => {
  window.dispatchEvent(new CustomEvent('courseClosed', {
    detail: { skillId: skill.id }
  }))
  
  onClose()
}

  const mobileClearProgress = async () => {
    try {
      const token = localStorage.getItem("currentUserToken")
      if (!token || !skill?.id) return

      const resetBody = new URLSearchParams()
      resetBody.append('userToken', token)
      resetBody.append('option', skill.id)

      const response = await POST('clearUserProgress', resetBody)

      if (response.status === 200) {
        localStorage.removeItem('clickedIndexes')
        localStorage.removeItem(`npsSubmitted_${skill.id}`)

        Object.keys(localStorage)
          .filter(k => k.startsWith("chatBackup_"))
          .forEach(k => localStorage.removeItem(k))

        setChatKey(prev => prev + 1)
      }
    } catch (error) {
      setChatKey(prev => prev + 1)
    }
  }

  if (!skill || !skill.title) {
    return null
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setMenuAnchor(event.currentTarget as HTMLElement)
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
  }

  const handleRestartCourse = async () => {
    try {
      handleMenuClose()

      if (clearProgress && typeof clearProgress === 'function') {
        await clearProgress()
      } else {
        await mobileClearProgress()
      }
    } catch (error) {
      setChatKey(prev => prev + 1)
    }
  }
  

  return (
    <ChatContainer>
      <ChatHeader>
        <BackButton onClick={handleClose}>
          <ArrowBack />
        </BackButton>

        <ProfileSection>
          <ProfileImage>
            <ImageComponent
              src={skill.thumbnail || skill.image || ''}
              alt={skill.title || 'Skill'}
              width="100%"
              height="100%"
              objectFit
            />
          </ProfileImage>

          <ProfileInfo>
            <ContactName>{skill.title || 'Loading...'}</ContactName>
            <LastSeen>{t('AI Learning Assistant')}</LastSeen>
          </ProfileInfo>
        </ProfileSection>

        <MenuButton
          onClick={handleMenuOpen}
          onTouchEnd={handleMenuOpen}
          sx={{
            zIndex: 1000,
            position: 'relative'
          }}
        >
          <MoreVert sx={{ fontSize: '24px' }} />
        </MenuButton>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          disableScrollLock
          sx={{
            zIndex: 100000,
            '& .MuiBackdrop-root': {
              backgroundColor: 'transparent'
            },
            '& .MuiMenu-paper': {
              backgroundColor: 'transparent !important',
              boxShadow: 'none !important',
              border: 'none !important'
            },
            '& .MuiList-root': {
              padding: '0 !important',
              backgroundColor: 'transparent !important'
            }
          }}
          PaperProps={{
            sx: {
              backgroundColor: 'transparent !important',
              boxShadow: 'none !important',
              border: 'none !important',
              borderRadius: 0,
              marginTop: '8px',
              minWidth: 'auto',
              '& .MuiList-root': {
                padding: 0,
                backgroundColor: 'transparent'
              }
            }
          }}
        >
          <MenuItem
            onClick={handleRestartCourse}
            sx={{
              backgroundColor: '#f1f3f4 !important', // Elegantes helles Grau
              backdropFilter: 'blur(20px)',
              borderRadius: '16px !important',
              padding: '16px 20px !important',
              fontSize: '16px !important',
              fontWeight: '500 !important',
              color: '#2D3748 !important',
              display: 'flex !important',
              alignItems: 'center !important',
              gap: '12px !important',
              border: 'none !important',
              boxShadow: 'none !important',
              minWidth: '180px !important',
              margin: '0 !important',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important',
              outline: 'none !important',
              
              '&:hover': {
                backgroundColor: '#e8eaed !important', // Etwas dunkler beim Hover
                transform: 'translateY(-1px) !important',
                boxShadow: 'none !important',
                outline: 'none !important',
              },
              
              '&:active': {
                transform: 'translateY(0px) !important',
                transition: 'all 0.1s !important',
                backgroundColor: '#dadce0 !important', // Noch dunkler beim Klick
                boxShadow: 'none !important',
                outline: 'none !important',
              },
              
              '&:focus': {
                backgroundColor: '#e8eaed !important', // Wie Hover
                boxShadow: 'none !important',
                outline: 'none !important',
              },
              
              '&::before, &::after': {
                display: 'none !important',
              },
              
              // iPhone Responsive
              '@media (max-width: 375px)': {
                padding: '14px 18px !important',
                fontSize: '15px !important',
                minWidth: '160px !important',
                gap: '10px !important',
                borderRadius: '14px !important',
              },
              
              '@media (max-width: 320px)': {
                padding: '12px 16px !important',
                fontSize: '14px !important',
                minWidth: '140px !important',
                gap: '8px !important',
                borderRadius: '12px !important',
              }
            }}
          >
            <RestartAlt
              sx={{
                fontSize: '22px !important',
                color: '#A1B7A5 !important',
                transition: 'transform 0.2s ease !important',
                
                '@media (max-width: 320px)': {
                  fontSize: '20px !important',
                }
              }}
            />
            <Typography
              sx={{
                fontSize: '16px !important',
                fontWeight: '500 !important',
                color: '#2D3748 !important',
                letterSpacing: '0.025em !important',
                
                '@media (max-width: 375px)': {
                  fontSize: '15px !important',
                },
                
                '@media (max-width: 320px)': {
                  fontSize: '14px !important',
                }
              }}
            >
              {t('Start Over')}
            </Typography>
          </MenuItem>
        </Menu>
      </ChatHeader>

      <MobileChatWrapper>
        <SkillsChatWrapper
          key={chatKey}
          active={skill}
          updateProgress={updateProgress}
          clearProgress={clearProgress}
          mobileMode={true}
        />
      </MobileChatWrapper>
    </ChatContainer>
  )
}

export default MobileSkillsDetailView